import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as AppService from '@/service/app'
import { saveTokenWithTimestamp } from '@/utils/helpers'
import { saveState } from '@/utils/localStorage'
import { browserTyoe } from '@/utils/browserType'

const emptyPermissionCodes = () => ({
  menuCodes: [],
  moduleCodes: [],
  buttonCodes: []
})

/**
 * 将 RBAC 码字段统一为 string[]。
 * 部分环境后端可能返回逗号分隔字符串（或非数组），直接判 Array 会得到 []，
 * 导致菜单/默认路由 `includes` 全失败，与本地（数组）表现不一致。
 */
function normalizeCodeListField (value) {
  if (value == null) return []
  if (Array.isArray(value)) {
    return value.map(String).map(s => s.trim()).filter(Boolean)
  }
  if (typeof value === 'string') {
    return value.split(',').map(s => s.trim()).filter(Boolean)
  }
  return []
}

/** 归一化接口返回的 PermissionCodesByTypeVO */
function normalizePermissionCodesPayload (payload) {
  const p = payload && typeof payload === 'object' ? payload : {}
  return {
    menuCodes: normalizeCodeListField(p.menuCodes),
    moduleCodes: normalizeCodeListField(p.moduleCodes),
    buttonCodes: normalizeCodeListField(p.buttonCodes)
  }
}

export const fetchPermissionCodesByType = createAsyncThunk(
  'app/fetchPermissionCodesByType',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AppService.getPermissionCodesByType()
      // Api 封装成功时：{ data: VO, meta }
      const raw = response?.data
      const vo = normalizePermissionCodesPayload(raw)
      return vo
    } catch (error) {
      console.error('fetchPermissionCodesByType 失败:', error)
      return rejectWithValue(error?.message || '获取权限码失败')
    }
  }
)

// 获取萤石云 Token
export const getYs7Token = createAsyncThunk(
  'app/getYs7Token',
  async (_, { rejectWithValue }) => {
    try {
      // 通过 Java 后端获取萤石云 token；不需要前端传 appKey/appSecret
      const response = await AppService.loginYs7Java()

      // 兼容两种返回：
      // 1) api.js 已格式化：response = { data: { accessToken, expireTime }, meta }
      // 2) 原始 axios：response.data = { code, msg, data: { accessToken, expireTime } }
      const maybeFormatted = response?.data
      const payload = maybeFormatted?.accessToken ? maybeFormatted : response?.data?.data

      if (payload?.accessToken) {
        return {
          accessToken: payload.accessToken,
          expireTime: payload.expireTime
        }
      }

      return rejectWithValue('获取萤石云 token 失败：响应格式错误')
    } catch (error) {
      console.error('获取萤石云 token 失败:', error)
      return rejectWithValue(error.message || '获取萤石云 token 失败')
    }
  }
)

// 获取 Java Token
export const getJavaToken = createAsyncThunk(
  'app/getJavaToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      // 通过 Java 后端登录接口获取 token；具体鉴权方式由后端决定（可能依赖 Cookie/网关等）
      const state = getState?.() || {}
      const userToken = state?.app?.user?.token
      // 兼容：某些情况下（如仅从 cookie 同步了 app.token，但 user 尚未就绪），用 app.token 兜底
      const appToken = state?.app?.token
      const params = userToken
        ? { token: userToken }
        : (appToken ? { token: appToken } : {})

      console.log('[getJavaToken] state:', state)
      console.log('[getJavaToken] userToken:', userToken)
      console.log('[getJavaToken] appToken:', appToken)
      console.log('[getJavaToken] params:', params)

      const response = await AppService.loginJava(params)

      // 兼容两种返回：
      // 1) api.js 已格式化：response = { data: { token, expiresIn }, meta }
      // 2) 原始 axios：response.data = { code, msg, data: { token, expiresIn } }
      const maybeFormatted = response?.data
      const payload = maybeFormatted?.token ? maybeFormatted : response?.data?.data

      if (payload?.token) {
        return {
          token: payload.token,
          expiresIn: payload.expiresIn
        }
      }

      // 保底：返回空结构，让 reducer 不会异常
      return { token: '', expiresIn: null }
    } catch (error) {
      console.error('获取 Java token 失败:', error)
      return rejectWithValue(error.message || '获取 Java token 失败')
    }
  }
)

// 初始状态
const initialState = {
  fetching: false,
  permissionCodes: emptyPermissionCodes(),
  permissionCodesFetching: false,
  permissionCodesLoaded: false,
  /** 拉取权限码失败（与「未加载」区分，供 /client 默认路由兜底，避免无限 Spin） */
  permissionCodesFetchFailed: false,
  token: '',
  user: {},
  browserType: {},
  ys7Token: '', // 萤石云 accessToken
  ys7TokenExpireTime: null, // 萤石云 token 过期时间戳（秒）
  javaToken: '', // Java token
  javaTokenExpireTime: null, // Java token 过期时间戳（秒）
}

// 创建 slice
const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // 重置应用状态
    resetAppState: () => initialState,

    // 应用登录
    appLogin: (state, action) => {
      const token = action.payload
      // MD5 token 无法解析用户信息，需要从登录响应中获取
      // 如果 action.payload 是对象，包含 token 和 user 信息
      if (typeof action.payload === 'object' && action.payload.token) {

        state.token = action.payload.token
        // 如果传入了 user 信息，使用传入的；否则保留现有的 user 信息
        // 这样可以确保 user 信息只能通过登录接口返回，不会被其他地方覆盖
        if (action.payload.user !== undefined) {
        state.user = action.payload.user || {}
        }
        // 如果 action.payload 中没有 user 字段，保留现有的 state.user（不覆盖）
        // 保存 token 及其创建时间，用于 7 天有效期验证
        saveTokenWithTimestamp(action.payload.token)
      } else {
        // 兼容旧代码：只有 token 字符串
        state.token = token

        // 如果 state 中已经有 user 信息，保留它（避免被覆盖）
        // 只有在没有 user 信息时才尝试从 token 解析
        // if (!state.user || Object.keys(state.user).length === 0) {
        //   state.user = parseToken(token) || {}
        // }
        // 保存 token 及其创建时间，用于 7 天有效期验证
        saveTokenWithTimestamp(token)
      }

      // 更新 localStorage
      const newState = { ...state, token: state.token, user: state.user }
      saveState({ app: newState })
    },

    // 获取浏览器类型
    getBrowserType: (state) => {
      state.browserType = browserTyoe()
    },

    /**
     * 微信小程序 web-view：URL 携带的 Java JWT（与 getJavaToken.fulfilled 同结构），避免 H5 再调 loginJava
     * payload: { token, expiresIn? 相对秒, expireTimeAbs? 绝对秒 }
     */
    setJavaTokenFromExternal: (state, action) => {
      const { token, expiresIn, expireTimeAbs } = action.payload || {}
      if (!token) return
      state.javaToken = token
      const expAbs = expireTimeAbs != null && expireTimeAbs !== '' ? Number(expireTimeAbs) : null
      if (Number.isFinite(expAbs) && expAbs > 0) {
        state.javaTokenExpireTime = Math.floor(expAbs)
      } else {
        const n = Number(expiresIn)
        state.javaTokenExpireTime = Number.isFinite(n) && n > 0
          ? Math.floor(Date.now() / 1000) + Math.floor(n)
          : null
      }
      const newState = { ...state }
      saveState({ app: newState })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissionCodesByType.pending, (state) => {
        state.permissionCodesFetching = true
        state.permissionCodesFetchFailed = false
      })
      .addCase(fetchPermissionCodesByType.fulfilled, (state, action) => {
        console.log('[fetchPermissionCodesByType.fulfilled] action.payload:', normalizePermissionCodesPayload(action.payload))
        state.permissionCodesFetching = false
        state.permissionCodesFetchFailed = false
        state.permissionCodesLoaded = true
        state.permissionCodes = normalizePermissionCodesPayload(action.payload)
        const newState = { ...state }
        saveState({ app: newState })
      })
      .addCase(fetchPermissionCodesByType.rejected, (state) => {
        state.permissionCodesFetching = false
        state.permissionCodesFetchFailed = true
        // 失败不将 permissionCodesLoaded 置为 true，避免误判为「已拉取过且为空权限」
      })

      // getYs7Token
      .addCase(getYs7Token.pending, (state) => {
        // 获取萤石云 token 时不设置 fetching，避免影响其他操作
      })
      .addCase(getYs7Token.fulfilled, (state, action) => {
        state.ys7Token = action.payload.accessToken
        state.ys7TokenExpireTime = action.payload.expireTime
        // 更新 localStorage
        const newState = { ...state }
        saveState({ app: newState })
      })
      .addCase(getYs7Token.rejected, (state, action) => {
        // 获取失败时不更新 state，保持原有 token（如果有）
        console.warn('获取萤石云 token 失败:', action.payload)
      })

      // getJavaToken // getYs7Token
      .addCase(getJavaToken.pending, (state) => {
        // 获取萤石云 token 时不设置 fetching，避免影响其他操作
      })
      .addCase(getJavaToken.fulfilled, (state, action) => {
        // thunk 返回：{ token, expiresIn }
        const token = action.payload?.token || ''
        const expiresInSec = Number(action.payload?.expiresIn)

        state.javaToken = token
        state.javaTokenExpireTime = Number.isFinite(expiresInSec) && expiresInSec > 0
          ? Math.floor(Date.now() / 1000) + expiresInSec
          : null
        const newState = { ...state }
        saveState({ app: newState })
        // 与小程序 storage 键名对齐，便于 web-view 二次进页读 localStorage 兜底
        try {
          if (typeof localStorage !== 'undefined' && token) {
            localStorage.setItem('java_token', token)
            if (state.javaTokenExpireTime != null && state.javaTokenExpireTime > 0) {
              localStorage.setItem('java_token_exp', String(state.javaTokenExpireTime))
            }
          }
        } catch (_) { /* ignore */ }
      })
      .addCase(getJavaToken.rejected, (state, action) => {
        console.warn('获取 Java token 失败:', action.payload)
      })
  }
})

// 导出 actions
export const { resetAppState, appLogin, getBrowserType, setJavaTokenFromExternal } = appSlice.actions

// 导出 initialState，供 store.js 使用（避免重复定义默认值）
export { initialState }

// 导出 reducer
export default appSlice.reducer
